import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import SeasonPassDTO from '../../dtos/SeasonPassDTO';
import cloneDeep from 'lodash/cloneDeep';
import RewardsTrackDTO from '../../../rewards-track/dtos/RewardsTrackDTO';
import DefaultTracks from '../../dtos/defaultTracks';
import { DataService } from 'src/app/services/data.service';
import { CommonEntityService } from 'src/app/common/services/common-entity.service';

@Component({
  selector: 'season-pass-form-tracks-custom-field',
  templateUrl: './season-pass-form-tracks-custom-field.component.html',
  styleUrls: ['./season-pass-form-tracks-custom-field.component.sass']
})
export class SeasonPassFormTracksCustomFieldComponent implements OnInit 
{
  @Input() seasonPassFreeTrack: any;
  @Input() seasonPassPaidTrack: any;
  @Input() seasonPassRecord: SeasonPassDTO = new SeasonPassDTO();
  @Input() freeOptions: Array<any> = [];
  @Input() paidOptions: Array<any> = [];
  @Input() isEdit: boolean = false;
  @Output() onChange = new EventEmitter<any>();
  @Output() onCreateRewardsTrackRecord = new EventEmitter<any>();
  @Output() onCancelRewardsTrackRecord = new EventEmitter<any>();
  freeTrack: RewardsTrackDTO = new RewardsTrackDTO();
  paidTrack: RewardsTrackDTO = new RewardsTrackDTO();
  defaultTracks: DefaultTracks = new DefaultTracks();
  isLoading: boolean = true;
  createNewFreeTrack: boolean = false;
  createNewPaidTrack: boolean = false;
  templateFreeRewardTracks: any;
  templatePaidRewardTracks: any;
  hideTemplateField: boolean = true;
  constructor(private dataService: DataService, private commonEntityService: CommonEntityService) { }

  /**
   * Fetches full rewards track data including all reward arrays.
   * Used because the list API (freeOptions/paidOptions) excludes reward arrays for performance.
   */
  private async fetchFullTrack(id: number): Promise<any> {
    try {
      return await this.commonEntityService.findOne('rewards-track/full', id);
    } catch (e) {
      return null;
    }
  }

  /**
   * Season Pass Form Tracks Custom Field Component Initialization
   */
  async ngOnInit()
  {
    if(this.seasonPassFreeTrack)
    {
      let freeTrack = this.freeOptions.find((x) => x._id == this.seasonPassFreeTrack);
      if (freeTrack && freeTrack.id) {
        this.freeTrack = await this.fetchFullTrack(freeTrack.id) || cloneDeep(freeTrack);
      } else {
        this.freeTrack = cloneDeep(freeTrack);
      }
    }
    if(this.seasonPassPaidTrack)
    {
      let paidTrack = this.paidOptions.find((x) => x._id == this.seasonPassPaidTrack);
      if (paidTrack && paidTrack.id) {
        this.paidTrack = await this.fetchFullTrack(paidTrack.id) || cloneDeep(paidTrack);
      } else {
        this.paidTrack = cloneDeep(paidTrack);
      }
    }

    this.templateFreeRewardTracks = await this.getTemplateFreeRewardsTrackOptions();
    this.templatePaidRewardTracks = await this.getTemplatePaidRewardsTrackOptions();

    this.isLoading = false;
  }

  /**
   * Handle Create Rewards Track Action
   * 
   * @param isFreeTrack Flag that sets whether or not is a new free track record.
   */
  async onCreateRewardsTrack(isFreeTrack: boolean)
  {
    if(isFreeTrack)
    {
      this.seasonPassRecord.free = null;
      this.freeTrack = new RewardsTrackDTO();
      
      // Transform the template data to match default structure
      const transformedFreeTrack = this.transformFreeTrack(this.templateFreeRewardTracks.free, this.templateFreeRewardTracks);
      const transformedStageMilestones = this.transformStageMilestones(this.templateFreeRewardTracks.stageMilestone);
      
      // Create a new rewards track with the required ref field
      this.freeTrack = {
        ...this.freeTrack,
        free: transformedFreeTrack,
        stageMilestone: transformedStageMilestones,
        type: 3,
        isTemplate: true,
      };
      
      console.log('final freeTrack with ref:', this.freeTrack);
      
      this.createNewFreeTrack = true;
    }
    else
    {
      this.seasonPassRecord.paid = null;
      this.paidTrack = new RewardsTrackDTO();
      this.paidTrack = {
        ...this.paidTrack,
        paid: this.templatePaidRewardTracks.paid,
        stageMilestone: this.templatePaidRewardTracks.stageMilestone,
        type: 4,
        isTemplate: true,
      };
      this.createNewPaidTrack = true;
    }

    this.onCreateRewardsTrackRecord.emit(this.seasonPassRecord);
  }

  /**
   * Handle Cancel Rewards Track Action
   * 
   * @param isFreeTrack Flag that sets whether or not is a free track record.
   */
  onCancelRewardsTrack(isFreeTrack: boolean)
  {
    if(isFreeTrack)
    {
      this.freeTrack = new RewardsTrackDTO();
      this.createNewFreeTrack = false;
    }
    else
    {
      this.paidTrack = new RewardsTrackDTO();
      this.createNewPaidTrack = false;
    }
    this.onCancelRewardsTrackRecord.emit(this.seasonPassRecord);
  }

  /**
   * Handle Changes on Season Pass Record Input variable
   * 
   * @param changes Record changes.
   */
  ngOnChanges(changes: SimpleChanges) 
  {
    if(changes.seasonPassFreeTrack && changes.seasonPassFreeTrack.previousValue != changes.seasonPassFreeTrack.currentValue)
    {
      this.handleTrackData(true, changes.seasonPassFreeTrack.currentValue);
    } 
    
    if(changes.seasonPassPaidTrack && changes.seasonPassPaidTrack.previousValue != changes.seasonPassPaidTrack.currentValue)
    {
      this.handleTrackData(false, changes.seasonPassPaidTrack.currentValue);
    } 
  }

  /**
   * Handles Track Data object
   *
   * @param isFreeTrack Flag that sets whether or not is a free track
   * @param value Track value.
   */
  async handleTrackData(isFreeTrack: boolean, value: any)
  {
    this.isLoading = true;
    if(isFreeTrack)
    {
      if(value)
      {
        let freeTrack = this.freeOptions.find((x) => x._id == value);
        if (freeTrack && freeTrack.id) {
          this.freeTrack = await this.fetchFullTrack(freeTrack.id) || cloneDeep(freeTrack);
        } else {
          this.freeTrack = cloneDeep(freeTrack);
        }

        if(this.freeTrack && this.freeTrack.id)
        {
          this.createNewFreeTrack = false;
        }
      }
    }
    else
    {
      if(value)
      {
        let paidTrack = this.paidOptions.find((x) => x._id == value);
        if (paidTrack && paidTrack.id) {
          this.paidTrack = await this.fetchFullTrack(paidTrack.id) || cloneDeep(paidTrack);
        } else {
          this.paidTrack = cloneDeep(paidTrack);
        }

        if(this.paidTrack && this.paidTrack.id)
        {
          this.createNewPaidTrack = false;
        }
      }
    }
    this.isLoading = false;
  }

  /**
   * Handle Record Changes from child component.
   *
   * @param field Field that changed.
   * @param event Event value.
   */
  onRecordChange(field: any, event: any)
  {
    this.onChange.emit({ field: field, value: field == 'freeObject' ? this.freeTrack : this.paidTrack });
  }

  async getTemplateFreeRewardsTrackOptions()
  {
    const templateFreeRewardTracks = await this.dataService.getDocumentAsync('rewards-track', {
      query: { 
        isTemplate: true,
        type: 3
      },  
      sort: { createdAt: -1 }
    });

    // Transform the stage milestones to match the default structure
    if (templateFreeRewardTracks && templateFreeRewardTracks.stageMilestone) {
      templateFreeRewardTracks.stageMilestone = this.transformStageMilestones(templateFreeRewardTracks.stageMilestone);
    }

    // Transform the free track to match the default structure
    if (templateFreeRewardTracks && templateFreeRewardTracks.free) {
      templateFreeRewardTracks.free = this.transformFreeTrack(templateFreeRewardTracks.free, templateFreeRewardTracks);
    }

    return templateFreeRewardTracks;
  }

  /**
   * Transforms the stage milestones array to match the default structure
   * @param stageMilestones Array of stage milestones to transform
   * @returns Transformed array of stage milestones
   */
  private transformStageMilestones(stageMilestones: number[]): number[] {
    if (!stageMilestones || stageMilestones.length === 0) {
      return this.defaultTracks.FreeDefaultStageMilestones;
    }

    // Ensure the array has the same length as the default
    const defaultLength = this.defaultTracks.FreeDefaultStageMilestones.length;
    const transformed = [...stageMilestones];

    // If the array is shorter than the default, pad it with the last value
    while (transformed.length < defaultLength) {
      transformed.push(transformed[transformed.length - 1] || 0);
    }

    // If the array is longer than the default, truncate it
    if (transformed.length > defaultLength) {
      transformed.length = defaultLength;
    }
    return transformed;
  }

  /**
   * Transforms the free track array to match the default structure
   * @param freeTrack Array of free track rewards to transform
   * @param templateData Template data for the free track
   * @returns Transformed array of free track rewards
   */
  private transformFreeTrack(freeTrack: any[], templateData: any): any[] {
    if (!freeTrack || freeTrack.length === 0) {
      return this.defaultTracks.FreeDefaultTrack;
    }

    // Create a deep copy of the default structure
    const transformed = JSON.parse(JSON.stringify(this.defaultTracks.FreeDefaultTrack));

    // For each stage in the default structure
    transformed.forEach((defaultStage: any[], index: number) => {
      const templateStage = freeTrack[index];
      
      if (templateStage && templateStage.length > 0) {
        // If we have a template stage, update the values while maintaining the structure
        defaultStage.forEach((defaultReward: any, rewardIndex: number) => {
          const templateReward = templateStage[rewardIndex];
          
          if (templateReward && templateReward.t && templateReward.id) {
            // Keep the t object as is
            defaultReward.t = { ...templateReward.t };
            
            // Transform the id object with only specific fields
            defaultReward.id = {
              apiControllerRoute: templateReward.id.apiControllerRoute || '',
              enabled: true,
              id: templateReward.id.id || '',
              name: templateReward.id.name || '',
              _id: templateReward.id._id || '',
              displayName: templateReward.id.displayName || ''
            };
            
            // Keep c and lineItemType as is (use != null to preserve c = 0)
            defaultReward.c = templateReward.c != null ? templateReward.c : '';
            defaultReward.lineItemType = templateReward.lineItemType || '';
          }
        });
      }
    });

    // Ensure the ref field is set to the template's _id
    transformed.ref = templateData._id;

    return transformed;
  }
  
  async getTemplatePaidRewardsTrackOptions()
  {
    const templatePaidRewardTracks = await this.dataService.getDocumentAsync('rewards-track', {
      query: { isTemplate: true, type: 4 },
      sort: { createdAt: -1 }
    });
    return templatePaidRewardTracks;
  }
}